import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Minus, Loader2 } from "lucide-react";
import ScopePicker, { useScopeOptions, MonthPicker, monthKey } from "@/components/ScopePicker";

const METRICS = [
  { key: "conversations", label: "Conversations" },
  { key: "pointed_to_solution", label: "Pointed to Solution" },
  { key: "contact_exchanges", label: "Contact Exchanges" },
  { key: "mg0s", label: "MG0s" },
  { key: "dtms", label: "DTMs" },
];

function emptyEntry() { return Object.fromEntries(METRICS.map((m) => [m.key, 0])); }

export default function Activity() {
  const { isReadOnly } = useAuth();
  const [entry, setEntry] = useState(emptyEntry());
  const [busy, setBusy] = useState(false);
  const [period, setPeriod] = useState("month");
  const { options: scopeOpts } = useScopeOptions();
  const [scope, setScope] = useState("personal");
  const [month, setMonth] = useState(monthKey());
  const [report, setReport] = useState(null);

  const load = async () => {
    const params = { scope, period };
    if (period === "month") params.month = month;
    const { data } = await api.get("/activity/report", { params });
    setReport(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, period, month]);
  useEffect(() => {
    if (!scopeOpts.length) return;
    const valid = new Set(scopeOpts.map((o) => o.value));
    if (!valid.has(scope)) setScope("personal");
  }, [scopeOpts, scope]);

  const total = METRICS.reduce((n, m) => n + (entry[m.key] || 0), 0);
  const save = async () => {
    if (total === 0) { toast.error("Add at least 1 activity before saving"); return; }
    setBusy(true);
    try {
      await api.post("/activity", entry);
      toast.success("Activity saved");
      setEntry(emptyEntry());
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const bump = (key, delta) => setEntry((e) => ({ ...e, [key]: Math.max(0, (e[key] || 0) + delta) }));

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Activity</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#0A1F44] mt-1">{isReadOnly ? "Activity" : "Log activity"}</h1>
        <p className="text-sm text-slate-500 mt-1">Fast numerical tracker — no prospect details. MG1s &amp; Launches auto-flow from your PBOs.</p>
      </div>

      {!isReadOnly && (
        <Card className="p-5 space-y-3" data-testid="log-activity-card">
          {METRICS.map((m) => (
            <div key={m.key} className="flex items-center justify-between gap-3">
              <Label className="text-sm text-slate-800">{m.label}</Label>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-9 w-9 p-0" onClick={() => bump(m.key, -1)}
                        data-testid={`dec-${m.key}`}><Minus className="w-4 h-4" /></Button>
                <Input type="number" min={0} value={entry[m.key]} onChange={(e) => setEntry({ ...entry, [m.key]: Math.max(0, parseInt(e.target.value || "0", 10)) })}
                       data-testid={`entry-${m.key}`} className="h-9 w-16 text-center" />
                <Button size="sm" className="h-9 w-9 p-0 bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90"
                        onClick={() => bump(m.key, 1)} data-testid={`inc-${m.key}`}><Plus className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-xs text-slate-500">Total: <span className="font-medium text-slate-800">{total}</span></div>
            <Button onClick={save} disabled={busy || total === 0} data-testid="save-activity"
                    className="rounded-full bg-[#0A1F44] text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save activity"}
            </Button>
          </div>
        </Card>
      )}

      <Card className="p-5" data-testid="activity-report">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-serif text-lg text-[#0A1F44]">Report</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <ScopePicker value={scope} onChange={setScope} options={scopeOpts} testid="activity-scope" />
            <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="activity-period-pills">
              {[["today","Today"],["week","This week"],["month","This month"]].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setPeriod(v)}
                  data-testid={`activity-period-${v}`}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                    period === v ? "bg-[#0A1F44] text-white" : "text-slate-700 hover:bg-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {period === "month" && (
              <MonthPicker value={month} onChange={setMonth} testid="activity-month" />
            )}
          </div>
        </div>
        {!report ? <div className="text-sm text-slate-500">Loading…</div> : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {["conversations","pointed_to_solution","contact_exchanges","mg0s","dtms","mg1s","launches"].map((k) => (
              <div key={k} className="rounded-lg border border-slate-200 p-3" data-testid={`funnel-${k}`}>
                <div className="text-[10px] uppercase tracking-wider text-[#C9A961] font-semibold">{k.replace("_"," ")}</div>
                <div className="font-serif text-2xl text-[#0A1F44] mt-1">{report.funnel[k] ?? 0}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
